"use client";

/**
 * Hero Renderer
 * Renders hero component preview in the canvas
 * Supports two background modes: full-bleed image/video with dark overlay, or media on solid color background
 */

import type React from "react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Calendar, MapPin } from "lucide-react";
import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import {
  DEFAULT_HERO_HEIGHT_VARIANT,
  HERO_HEIGHT_CLASSES,
  HERO_IMAGE_RENDER_QUALITY,
  HERO_IMAGE_RENDER_WIDTH,
} from "../../../_logic/constants";
import { transformStorageImageUrl } from "../../../_logic/image-cdn";
import { isSafeUrl } from "../../../_logic/utils";
import { formatHeroDate } from "../../../_logic/utils/hero-date";
import { useBuilderStore } from "../../../_stores/builder-store";
import type { HeroComponent } from "../../../_types";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { SmartButton } from "../../public/SmartButton";
import { EmailLinkIndicator } from "./_shared/EmailLinkIndicator";
import HeroCalendarDropdown from "./_shared/HeroCalendarDropdown";
import { InlineTextEdit } from "./_shared/InlineTextEdit";
import { InlineTipTapEdit } from "./_shared/InlineTipTapEdit";

interface HeroRendererProps {
  component: HeroComponent;
}

/**
 * Renders a hero section preview
 */
export function HeroRenderer({ component }: HeroRendererProps): React.ReactElement {
  const {
    id: componentId,
    title,
    startDate,
    startTime,
    endDate,
    endTime,
    date: legacyDate,
    location,
    description,
    backgroundMedia,
    backgroundMediaType,
    overlayOnBackground,
    showCtaButtons = true,
    ctaButtons,
    heightVariant = DEFAULT_HERO_HEIGHT_VARIANT,
  } = component;
  const { primaryColor, eventId, timeZone } = useBuilderContext();
  const { isPublic } = usePublicViewContext();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const calendarContainerRef = useRef<HTMLDivElement>(null);
  const calendarTriggerRef = useRef<HTMLButtonElement>(null);
  const calendarDropdownId = useId();
  const handleToggleCalendar = useCallback(() => setIsCalendarOpen((prev) => !prev), []);
  const handleCloseCalendar = useCallback(() => setIsCalendarOpen(false), []);
  // Recompute the "today" verdict whenever the tab regains focus so a page left open across
  // midnight (or the event's end day) reflects current state without a full reload.
  const [todayTick, setTodayTick] = useState(0);
  useEffect(() => {
    function handleVisibility(): void {
      if (document.visibilityState === "visible") setTodayTick((t) => t + 1);
    }
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);
  // Don't offer "Add to calendar" for events whose end date is already in the past. Falls back
  // to startDate when endDate isn't set — multi-day events stay clickable through the final day.
  // Computes "today" in the event's IANA timezone (when set) so cross-timezone viewers don't
  // see the trigger flip at their local midnight instead of the event's.
  const isPastEvent = useMemo(() => {
    if (!startDate) return false;
    const lastDay = endDate ?? startDate;
    const now = new Date();
    let today: string;
    try {
      // 'en-CA' locale formats Gregorian dates as YYYY-MM-DD, suitable for direct string comparison.
      today = new Intl.DateTimeFormat("en-CA", {
        timeZone: timeZone || undefined,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(now);
    } catch {
      // Invalid IANA name slipped through — fall back to viewer local time.
      today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    }
    return lastDay < today;
    // todayTick is intentionally included so visibility-change re-evaluates the comparison.
  }, [startDate, endDate, timeZone, todayTick]);
  const showCalendarTrigger = isPublic && Boolean(startDate) && !isPastEvent;
  const calendarEvent = useMemo(
    () => ({
      title: title || "",
      date: startDate ?? null,
      endDate: endDate ?? null,
      startTime: startTime ?? null,
      endTime: endTime ?? null,
      location: location ?? null,
      timeZone,
    }),
    [title, startDate, endDate, startTime, endTime, location, timeZone]
  );
  const titleSizeClass = isPublic ? "md:text-4xl lg:text-5xl" : "@md:text-4xl @lg:text-5xl";
  const metaLayoutClass = isPublic ? "sm:flex-row sm:gap-4" : "@sm:flex-row @sm:gap-4";
  // Hero fills full viewport if no navbar is visible, otherwise the area below the sticky navbar.
  const hasVisibleNavbar = useBuilderStore((s) => {
    const nav = s.components.find((c) => c.type === "navbar");
    return nav?.type === "navbar" && nav.visible;
  });

  // Structured fields take precedence; fall back to the legacy free-text `date` so
  // pre-2026-05 pages without start/end fields keep rendering exactly as before.
  const formattedDate = useMemo(() => {
    if (startDate || endDate) {
      return formatHeroDate({ startDate, startTime, endDate, endTime, timeZone: timeZone });
    }
    return legacyDate ?? "";
  }, [startDate, startTime, endDate, endTime, legacyDate, timeZone]);
  const hasDate = Boolean(formattedDate);

  // Derived values
  const safeMedia = backgroundMedia && isSafeUrl(backgroundMedia) ? backgroundMedia : null;
  const isVideo = backgroundMediaType === "video";
  const encodedMediaUrl = useMemo(() => {
    if (!safeMedia) return null;
    // Images route through the Supabase transform CDN (right-sized WebP); videos are streamed as-is.
    const resolved = isVideo
      ? safeMedia
      : transformStorageImageUrl(safeMedia, {
          width: HERO_IMAGE_RENDER_WIDTH,
          quality: HERO_IMAGE_RENDER_QUALITY,
        });
    return encodeURI(resolved).replace(/['()]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`);
  }, [safeMedia, isVideo]);

  const backgroundStyle = useMemo(() => {
    const colorGradient = `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 50%, ${primaryColor}bb 100%)`;

    if (overlayOnBackground && encodedMediaUrl) {
      return { background: colorGradient };
    }

    // Videos use a <video> element instead of CSS background
    if (encodedMediaUrl && !isVideo) {
      return {
        background: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.6)), url('${encodedMediaUrl}')`,
        backgroundSize: "cover" as const,
        backgroundPosition: "center" as const,
      };
    }

    return { background: colorGradient };
  }, [encodedMediaUrl, isVideo, overlayOnBackground, primaryColor]);

  // Falls back to the default variant's classes for a value outside HERO_HEIGHT_CLASSES (e.g.
  // stored before the field was validated) rather than trusting `layout_tree`, which has no
  // schema enforcement on write.
  const heightClasses = HERO_HEIGHT_CLASSES[heightVariant] ?? HERO_HEIGHT_CLASSES[DEFAULT_HERO_HEIGHT_VARIANT];
  const heightClass = hasVisibleNavbar ? heightClasses.withNavbar : heightClasses.withoutNavbar;

  // Render
  return (
    <div
      className={`relative flex items-center justify-center overflow-hidden text-white ${heightClass}`}
      style={backgroundStyle}
    >
      {/* Video background — autoplay, muted, looped for ambient effect */}
      {isVideo && encodedMediaUrl && (
        <video
          src={encodedMediaUrl}
          className="absolute inset-0 h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          aria-hidden="true"
        />
      )}

      {/* Dark overlay for video (non-overlay mode) to ensure text readability */}
      {isVideo && encodedMediaUrl && !overlayOnBackground && <div className="absolute inset-0 bg-black/50" />}

      {/* Overlay mode: full-bleed image layer on top of color gradient (images only — videos use the <video> above) */}
      {overlayOnBackground && encodedMediaUrl && !isVideo && (
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url('${encodedMediaUrl}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      )}
      <div className="relative z-10 mx-auto flex max-w-4xl flex-col gap-5 px-6 py-16 text-center">
        <InlineTextEdit
          tag="h1"
          value={title || ""}
          placeholder="Your Event Title"
          className={`text-3xl font-black tracking-tight ${titleSizeClass}`}
          componentId={componentId}
          fieldId="hero-title"
          field="title"
        />

        {(hasDate || location || !isPublic) && (
          <div className={`flex flex-col items-center justify-center gap-2 text-xl ${metaLayoutClass}`}>
            {(hasDate || !isPublic) && (
              <div ref={calendarContainerRef} className="relative text-center">
                {showCalendarTrigger ? (
                  <button
                    ref={calendarTriggerRef}
                    type="button"
                    onClick={handleToggleCalendar}
                    aria-expanded={isCalendarOpen}
                    aria-haspopup="menu"
                    aria-label="Add to calendar"
                    aria-controls={isCalendarOpen ? calendarDropdownId : undefined}
                    className="group inline cursor-pointer rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
                  >
                    <Calendar
                      className="mr-[0.2em] inline-block h-[0.85em] w-[0.85em] align-[-0.1em]"
                      aria-hidden="true"
                    />
                    <span
                      className={`font-semibold underline-offset-2 [@media(hover:hover)]:group-hover:underline ${hasDate ? "" : "italic opacity-60"}`}
                    >
                      {hasDate ? formattedDate : "Event Date"}
                    </span>
                  </button>
                ) : (
                  <>
                    <Calendar
                      className="mr-[0.2em] inline-block h-[0.85em] w-[0.85em] align-[-0.1em]"
                      aria-hidden="true"
                    />
                    <span className={`font-semibold ${hasDate ? "" : "italic opacity-60"}`}>
                      {hasDate ? formattedDate : "Event Date"}
                    </span>
                  </>
                )}
                {isCalendarOpen && showCalendarTrigger && (
                  <HeroCalendarDropdown
                    id={calendarDropdownId}
                    event={calendarEvent}
                    containerRef={calendarContainerRef}
                    triggerRef={calendarTriggerRef}
                    onClose={handleCloseCalendar}
                  />
                )}
              </div>
            )}
            {(location || !isPublic) && (
              <div className="text-center">
                <MapPin className="mr-[0.2em] inline-block h-[0.85em] w-[0.85em] align-[-0.1em]" aria-hidden="true" />
                <InlineTextEdit
                  tag="span"
                  value={location || ""}
                  placeholder="Event Location"
                  className="font-semibold leading-tight"
                  componentId={componentId}
                  fieldId="hero-location"
                  field="location"
                />
              </div>
            )}
          </div>
        )}

        {(description || !isPublic) && (
          <InlineTipTapEdit
            content={description || ""}
            className="prose prose-base mx-auto max-w-2xl font-medium text-white opacity-95 prose-headings:text-white prose-p:text-white prose-strong:text-white prose-a:text-white prose-li:text-white prose-ul:text-white prose-ol:text-white prose-blockquote:text-white"
            componentId={componentId}
            fieldId="hero-description"
            field="description"
            placeholder="Add a description..."
          />
        )}

        {showCtaButtons && ctaButtons && ctaButtons.length > 0 && (
          <div className="flex flex-wrap justify-center gap-3 pt-2">
            {ctaButtons.map((cta) => {
              const buttonColor = cta.color ?? primaryColor;
              const buttonTextColor = getButtonTextColor(buttonColor);

              // Public view: only render a real, clickable control when a link is set. A
              // url-less CTA used to render as a button-styled but inert <span> that looked
              // exactly like a working button — hide it so visitors never click a dead control.
              if (isPublic) {
                if (!cta.url) return null;
                return (
                  <SmartButton
                    key={cta.id}
                    href={cta.url}
                    label={cta.label || "Button"}
                    primaryColor={buttonColor}
                    eventId={eventId}
                    buttonId={cta.id}
                    className="rounded-xl px-8 py-3 text-base font-semibold shadow-lg transition-opacity hover:opacity-90"
                    style={{ backgroundColor: buttonColor, color: buttonTextColor }}
                  />
                );
              }

              // Builder preview: keep the button visible so organizers can select and edit it,
              // but flag ones with no link (dashed outline + tooltip) so the dead control is
              // caught at authoring time rather than shipped.
              const hasLink = Boolean(cta.url);
              return (
                <span
                  key={cta.id}
                  title={hasLink ? undefined : "No link set — this button won't be shown on the live page"}
                  className={`inline-flex items-center gap-2 rounded-xl px-8 py-3 text-base font-semibold shadow-lg${
                    hasLink ? "" : " opacity-70 outline-dashed outline-2 outline-amber-400"
                  }`}
                  style={{ backgroundColor: buttonColor, color: buttonTextColor }}
                >
                  <EmailLinkIndicator href={cta.url} />
                  {cta.label || "Button"}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
